<script setup lang="ts">
import { Monitor, Moon, Sun } from 'lucide-vue-next';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import { Button } from '@/components/ui/button';
import { SidebarTrigger } from '@/components/ui/sidebar';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAppearance } from '@/composables/useAppearance';
import { useSentryFeedback } from '@/composables/useSentryFeedback';
import type { BreadcrumbItem } from '@/types';
import type { Appearance } from '@/types';

withDefaults(
    defineProps<{
        breadcrumbs?: BreadcrumbItem[];
    }>(),
    {
        breadcrumbs: () => [],
    },
);

const { openFeedbackForm } = useSentryFeedback();
const { appearance, updateAppearance } = useAppearance();

const CYCLE: Appearance[] = ['light', 'dark', 'system'];
const ICONS = { light: Sun, dark: Moon, system: Monitor } as const;
const LABELS = {
    light: 'Thème clair',
    dark: 'Thème sombre',
    system: 'Thème système',
} as const;

function cycleTheme() {
    const next = CYCLE[(CYCLE.indexOf(appearance.value) + 1) % CYCLE.length];
    updateAppearance(next);
}
</script>

<template>
    <header
        class="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-border/30 px-6 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:px-4"
    >
        <div class="flex items-center gap-2">
            <SidebarTrigger class="-ml-1" />
            <template v-if="breadcrumbs && breadcrumbs.length > 0">
                <Breadcrumbs :breadcrumbs="breadcrumbs" />
            </template>
        </div>

        <div class="flex items-center gap-2">
            <!-- Theme toggle -->
            <TooltipProvider :delay-duration="300">
                <Tooltip>
                    <TooltipTrigger as-child>
                        <Button
                            variant="ghost"
                            size="icon"
                            class="h-8 w-8 text-muted-foreground hover:text-foreground"
                            @click="cycleTheme"
                        >
                            <component
                                :is="ICONS[appearance]"
                                class="h-4 w-4"
                            />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p>{{ LABELS[appearance] }} — cliquer pour changer</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            <!-- Feedback -->
            <Button
                variant="outline"
                size="sm"
                class="h-7 gap-1.5 border-orange-300 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950"
                title="Signaler un problème ou proposer une amélioration"
                @click="openFeedbackForm"
            >
                <span>Signaler · Suggérer</span>
            </Button>
        </div>
    </header>
</template>
